import styled, {
  css,
  FlattenInterpolation,
  ThemeProps,
  DefaultTheme,
} from 'styled-components'
import { Variant } from './types'
import { tone } from 'utils'
import { transparentize } from 'polished'
interface BaseButtonProps {
  variant?: Variant
  disabled?: boolean
  success?: boolean
  error?: boolean
  loading?: boolean
  small?: boolean
  smaller?: boolean
  tiny?: boolean
  fill?: boolean
}

const ignoredProps = [
  'active',
  'loading',
  'small',
  'smaller',
  'fill',
  'tiny',
  'variant',
  'Icon',
]

const isDisabled = (style: FlattenInterpolation<ThemeProps<DefaultTheme>>) => ({
  disabled,
  success,
  error,
  loading,
}: BaseButtonProps) => disabled && !success && !error && !loading && style

const LoadingWrapper = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`

const BaseButton = styled.button.withConfig({
  shouldForwardProp: prop => !ignoredProps.includes(prop),
})<BaseButtonProps>(
  ({
    theme: { colors, fontSizes, fontWeights, radii },
    small,
    smaller,
    tiny,
    fill,
    loading,
  }) => css`
    /* Font */
    font-size: ${fontSizes.small};
    font-weight: ${fontWeights.semibold};

    /* Text */
    text-align: left;
    white-space: nowrap;
    line-height: 1;

    /* Layout */
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: ${() => `6px 12px` /* eslint-disable-line */};
    min-height: 48px;

    /* Border */
    border-radius: ${small ? radii.large : radii.xlarge};
    border: 1px solid transparent;

    /* Color */
    color: ${colors.foreground.primary[1]};

    /* Prevents Chrome flicker on modal - https://github.com/sketch-hq/Cloud/issues/3723 */
    backface-visibility: hidden;

    /* Other */
    user-select: none;
    box-shadow: 0 1px 2px 0 ${colors.shadow.outer};
    cursor: pointer;
    appearance: none;

    /* Special cases */
    & + button,
    & + a {
      margin-left: 16px;
    }

    ${small &&
    css`
      font-size: ${fontSizes.smaller};
      padding: 4px 16px;
      min-height: 40px;
    `};

    ${smaller &&
    css`
      font-size: ${fontSizes.small};
      padding: 4px 16px;
      min-height: 38px;
    `};

    ${tiny &&
    css`
      font-size: ${fontSizes.little};
      padding: 4px 12px;
      min-height: 32px;
    `};

    &:hover {
      color: ${colors.foreground.primary[1]};
    }

    &:active {
      color: ${colors.text.light.secondary};
    }

    &[disabled] {
      cursor: not-allowed;
      pointer-events: none;
    }

    ${fill &&
    css`
      display: flex;
      width: 100%;
    `};

    ${loading &&
    css`
      &&& {
        color: transparent;
        overflow: hidden;

        &[disabled] {
          opacity: 1;
        }
      }
    `};
  `
)

const PrimaryButton = styled(BaseButton)(
  ({ theme: { colors } }) => css`
    color: ${colors.foreground.primary[1]};
    background-color: ${colors.primaryTint[100]};

    &:hover {
      background-color: ${tone(1, colors.primaryTint[100])};
    }

    &:active {
      background-color: ${tone(2, colors.primaryTint[100])};
    }

    ${isDisabled(
      css`
        color: ${colors.foreground.primary[2]};
        background-color: ${colors.primaryTint[30]};
      `
    )};
  `
)

const SecondaryButton = styled(BaseButton)(
  ({ theme: { colors } }) => css`
    color: ${colors.primaryTint[100]};
    background-color: ${colors.background.secondary[1]};
    border: 1px solid ${colors.border[1]};

    &:hover {
      color: ${colors.primaryTint[100]};
      background-color: ${colors.background.secondary[3]};
    }

    &:active {
      color: ${colors.primaryTint[100]};
      background-color: ${colors.background.secondary[3]};
    }

    ${isDisabled(
      css`
        color: ${colors.primaryTint[30]};
        background-color: ${colors.background.secondary[1]};
      `
    )};
  `
)

const TertiaryButton = styled(BaseButton)(
  ({ theme: { colors } }) => css`
    box-shadow: none;

    color: ${colors.foreground.secondary[1]};
    background-color: ${colors.background.tertiary[1]};

    &:hover {
      color: ${colors.foreground.secondary[1]};
      background-color: ${colors.background.tertiary[2]};
    }

    &:active {
      color: ${colors.foreground.secondary[1]};
      background-color: ${colors.background.tertiary[3]};
    }

    ${isDisabled(
      css`
        color: ${colors.foreground.secondary[4]};
        background-color: ${colors.background.tertiary[1]};
      `
    )};
  `
)

const PrimaryButtonUntinted = styled(BaseButton)(
  ({ theme: { colors } }) => css`
    color: ${colors.foreground.primary[1]};
    background-color: ${colors.background.primary[2]};

    &:hover {
      background-color: ${colors.background.primary[1]};
    }

    &:active {
      color: ${colors.foreground.primary[2]};
      background-color: ${colors.background.primary[1]};
    }

    ${isDisabled(
      css`
        color: ${colors.foreground.primary[2]};
        background-color: ${transparentize(0.5, colors.background.primary[2])};
      `
    )};
  `
)

const SecondaryButtonUntinted = styled(BaseButton)(
  ({ theme: { colors } }) => css`
    color: ${colors.foreground.secondary[1]};
    background-color: ${colors.background.secondary[1]};
    border: 1px solid ${colors.border[1]};

    &:hover {
      color: ${colors.foreground.secondary[1]};
      background-color: ${colors.background.secondary[3]};
    }

    &:active {
      background-color: ${colors.background.secondary[3]};
    }

    ${isDisabled(
      css`
        color: ${colors.foreground.secondary[4]};
      `
    )};
  `
)

const ErrorButton = styled(BaseButton)(
  ({ theme: { colors } }) => css`
    color: ${colors.foreground.primary[1]};
    background-color: ${colors.state.negative};
    box-shadow: 0 1px 5px 0 rgba(191, 28, 28, 0.25);

    &:hover {
      background-color: ${tone(1, colors.danger)};
    }

    &:active {
      background-color: ${tone(2, colors.danger)};
    }

    ${isDisabled(
      css`
        background-color: ${tone(-6, colors.tungsten)};
        box-shadow: 0 1px 5px 0 rgba(109, 114, 120, 0.25);
      `
    )};
  `
)

const SecondaryErrorButton = styled(BaseButton)(
  ({ theme: { colors } }) => css`
    color: ${colors.state.negative};
    background-color: ${colors.background.secondary[1]};
    border: 1px solid ${colors.border[1]};

    &:hover {
      color: ${colors.state.negative};
      background-color: ${colors.background.secondary[2]};
    }

    &:active {
      color: ${colors.state.negative};
      background-color: ${colors.background.secondary[3]};
    }

    ${isDisabled(
      css`
        color: ${colors.state.negative};
        background-color: ${colors.background.secondary[1]};
      `
    )};
  `
)

const getButton = (variant: Variant) => {
  switch (variant) {
    case 'primary':
      return PrimaryButton
    case 'primary-untinted':
      return PrimaryButtonUntinted
    case 'secondary':
      return SecondaryButton
    case 'secondary-untinted':
      return SecondaryButtonUntinted
    case 'tertiary':
      return TertiaryButton
    case 'negative':
      return ErrorButton
    case 'negative-secondary':
      return SecondaryErrorButton
    default:
      return SecondaryButton
  }
}

const IconWrapper = styled.span`
  position: relative;
  top: 2px;
  margin-right: 8px;
`

const inlineFlexCenter = css`
  display: inline-flex;
  justify-content: center;
  align-items: center;
`

const ButtonWrapper = styled.div`
  ${inlineFlexCenter}
`

const ButtonIcon = styled.div`
  ${inlineFlexCenter}

  margin-right: 4px;
`

export {
  ButtonIcon,
  IconWrapper,
  ButtonWrapper,
  BaseButton,
  getButton,
  ignoredProps,
  LoadingWrapper,
}
